/**
 * Admin Explore Destinations page — manage the "Explore Dominica" rail
 * in the passenger app (emoji, title, subtitle, stop, order, on/off).
 *
 * Changes appear in the app within minutes (riders fetch once per app
 * session) — no app release needed.
 *
 * CRITICAL FIELD: "Bus stop" must EXACTLY match a bookable stop name in
 * the app's government fare table (e.g. "Trafalgar", "Mero", "Scotts
 * Head"). The app silently hides any card whose stop it can't book, so
 * a typo never breaks the rider experience — the card just won't show.
 * The form surfaces this rule prominently.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Compass, Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';

interface DestRow {
  id: string;
  emoji: string;
  title: string;
  subtitle: string | null;
  stop_name: string;
  sort_order: number;
  is_active: boolean;
  updated_at: string;
}

interface FormState {
  id: string | null;
  emoji: string;
  title: string;
  subtitle: string;
  stop_name: string;
  sort_order: number;
}

const EMPTY_FORM: FormState = {
  id: null,
  emoji: '📍',
  title: '',
  subtitle: '',
  stop_name: '',
  sort_order: 100,
};

export default function ExploreDestinations() {
  const { isManager } = useAdminAuth();
  const [rows, setRows] = useState<DestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('admin_list_explore_destinations');
    const result = data as { ok?: boolean; rows?: DestRow[]; error?: string } | null;
    if (error || !result?.ok) {
      toast.error(`Failed to load: ${error?.message ?? result?.error ?? 'unknown'}`);
    } else {
      setRows(result.rows ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!form) return;
    if (!form.title.trim() || !form.stop_name.trim() || !form.emoji.trim()) {
      toast.error('Emoji, title, and bus stop are required.');
      return;
    }
    setSaving(true);
    const { data, error } = await supabase.rpc('admin_upsert_explore_destination', {
      p_id: form.id,
      p_emoji: form.emoji.trim(),
      p_title: form.title.trim(),
      p_subtitle: form.subtitle.trim() || null,
      p_stop_name: form.stop_name.trim(),
      p_sort_order: form.sort_order,
      p_is_active: true,
    });
    setSaving(false);
    const result = data as { ok?: boolean; error?: string } | null;
    if (error || !result?.ok) {
      toast.error(`Save failed: ${error?.message ?? result?.error ?? 'unknown'}`);
      return;
    }
    toast.success(form.id ? 'Destination updated.' : 'Destination added.');
    setForm(null);
    void load();
  };

  const toggleActive = async (r: DestRow) => {
    setBusyId(r.id);
    const { data, error } = await supabase.rpc('admin_upsert_explore_destination', {
      p_id: r.id,
      p_emoji: r.emoji,
      p_title: r.title,
      p_subtitle: r.subtitle,
      p_stop_name: r.stop_name,
      p_sort_order: r.sort_order,
      p_is_active: !r.is_active,
    });
    setBusyId(null);
    const result = data as { ok?: boolean; error?: string } | null;
    if (error || !result?.ok) {
      toast.error(`Update failed: ${error?.message ?? result?.error ?? 'unknown'}`);
      return;
    }
    void load();
  };

  const remove = async (r: DestRow) => {
    if (!window.confirm(`Delete "${r.title}"? Riders stop seeing it within minutes.`)) {
      return;
    }
    setBusyId(r.id);
    const { data, error } = await supabase.rpc('admin_delete_explore_destination', {
      p_id: r.id,
    });
    setBusyId(null);
    const result = data as { ok?: boolean; error?: string } | null;
    if (error || !result?.ok) {
      toast.error(`Delete failed: ${error?.message ?? result?.error ?? 'unknown'}`);
      return;
    }
    toast.success('Destination deleted.');
    void load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Compass className="w-6 h-6 text-emerald-500" />
            Explore Destinations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            The "Explore Dominica" cards in the rider app. Add sights, reorder,
            or hide them. Changes reach riders within minutes, with no app
            update needed.
          </p>
        </div>
        <Button onClick={() => setForm({ ...EMPTY_FORM })}>
          <Plus className="w-4 h-4 mr-1" />
          Add destination
        </Button>
      </div>

      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Order</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Bus stop</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className={r.is_active ? '' : 'opacity-50'}>
                    <TableCell className="text-gray-400">{r.sort_order}</TableCell>
                    <TableCell>
                      <span className="text-lg mr-2">{r.emoji}</span>
                      <span className="font-medium">{r.title}</span>
                      {r.subtitle ? (
                        <span className="text-gray-400 text-xs ml-2">{r.subtitle}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.stop_name}</TableCell>
                    <TableCell>
                      {r.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Live
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Hidden</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === r.id}
                          onClick={() => void toggleActive(r)}
                          title={r.is_active ? 'Hide from riders' : 'Show to riders'}
                        >
                          {r.is_active ? (
                            <EyeOff className="w-3.5 h-3.5" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setForm({
                              id: r.id,
                              emoji: r.emoji,
                              title: r.title,
                              subtitle: r.subtitle ?? '',
                              stop_name: r.stop_name,
                              sort_order: r.sort_order,
                            })
                          }
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {isManager && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            disabled={busyId === r.id}
                            onClick={() => void remove(r)}
                            aria-label={`Delete ${r.title}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-400 py-8">
                      No destinations yet. The app shows its built-in list until you
                      add some.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={form !== null} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {form?.id ? 'Edit destination' : 'Add destination'}
            </DialogTitle>
            <DialogDescription>
              Riders see this as a card in the "Explore Dominica" rail. One tap
              sets their drop-off to the bus stop below.
            </DialogDescription>
          </DialogHeader>
          {form ? (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Emoji</label>
                  <Input
                    value={form.emoji}
                    onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                    className="text-center text-lg"
                    maxLength={8}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Title (what riders call the sight)
                  </label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Emerald Pool"
                    maxLength={40}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Subtitle (one-line hook, optional)
                </label>
                <Input
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  placeholder="Rainforest swimming hole"
                  maxLength={60}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Bus stop (must EXACTLY match a fare-table stop)
                </label>
                <Input
                  value={form.stop_name}
                  onChange={(e) => setForm({ ...form, stop_name: e.target.value })}
                  placeholder="Trafalgar"
                  maxLength={80}
                />
                <p className="text-[11px] text-amber-600 mt-1">
                  ⚠ Spelling matters: the app hides any card whose stop doesn't
                  match a bookable stop in the government fare table (e.g.
                  "Trafalgar", "Mero", "Scotts Head", "Kalinago Territory"). A
                  typo won't break anything. The card just won't appear.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Sort order (lower = earlier in the rail)
                </label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm({ ...form, sort_order: Number(e.target.value) || 100 })
                  }
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : form?.id ? 'Save changes' : 'Add destination'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
